import * as React from 'react'
import { connect } from 'react-redux'
import {
  Box,
  Button,
  Paper,
  Icon,
  Collapse,
  Typography,
  MenuItem,
  Menu,
} from '@design/pt-ui'

import FormatsetForm from '../../components/FormatsetForm'
import { Project, ProjectId } from '../../interfaces/entities/project'
import { Creative, CreativeAction } from '../../interfaces/entities/creative'
import SortableFormatsetComponent from '../../components/Formatset/Formatsets'
import { Formatset, FormatsetId } from '../../interfaces/entities/formatset'
import { User } from '../../interfaces/entities/user'
import { translateWithUserLocale } from '../../utils/i18n'
import Loader from '../../components/Loader'

import { State } from '../../model'

import CreativeFilter from './Filter'
import CollapseButton from '../../components/CollapseButton'
import { useCreativeFilters } from './hooks'

import FormatsetImport from './FormatsetImport'
import { Platform } from '../../interfaces/entities/platform'

import OutdatedFormatsetsMessageDialog from './OutdatedFormatsetsMessageDialog'

interface IProps {
  project: Project
  user: User
  doCreativeAction: (creative: Creative, action: CreativeAction) => void
  onAddFormatsets: (formatsets: Formatset[]) => any
  onRemoveFormatSet: (formatsetId: FormatsetId) => any
  getProject: (id: ProjectId) => void
  platforms: Platform[]
  makingCreative?: boolean
}
function Main(props: IProps) {
  const [state, setState] = React.useState({
    importFormatsetsDialogOpened: false,
    importFormatsetMessageOpen: false,
    invalidFormatsetsText: '',
  })

  const openImportFormatsetsDialog = () => {
    setState((state) => ({
      ...state,
      importFormatsetsDialogOpened: true,
    }))
  }

  const closeImportFormatsetsDialog = () => {
    setState((state) => ({
      ...state,
      importFormatsetsDialogOpened: false,
    }))
  }

  const [expanded, setExpanded] = React.useState(true)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [selectedPlatform, setSelectedPlatform] = React.useState<string>('')

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }
  const handleClickAddSpecification = (event: any) => {
    handleClosePlatformsMenu()
    setAnchorEl(event.currentTarget)
  }
  const handleSelectPlatform = (platform: string) => {
    setSelectedPlatform(platform)
    handleClosePlatformsMenu()
  }
  const handleClosePlatformsMenu = () => {
    setAnchorEl(null)
  }
  const openImportFormatsetMessageDialog = (invalidFormatsetsText: string) => {
    setState((state) => ({
      ...state,
      importFormatsetMessageOpen: true,
      invalidFormatsetsText,
    }))
  }
  const closeImportFormatsetMessageDialog = () => {
    setState((state) => ({
      ...state,
      importFormatsetMessageOpen: false,
    }))
  }

  const creativesFilter = useCreativeFilters()

  return (
    <div className="project-view-formatsets">
      {props.makingCreative ? <Loader variant="overlay" /> : null}

      {props.user.groups.includes('client') ? null : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingX: 2,
            paddingBottom: '10px',
          }}>
          <FormatsetImport
            open={state.importFormatsetsDialogOpened}
            onOpen={openImportFormatsetsDialog}
            onClose={closeImportFormatsetsDialog}
            project={props.project}
            platforms={props.platforms}
            getProject={props.getProject}
            activator={
              props.project.status.name === 'archived' ? (
                ''
              ) : (
                <Button size={'small'} color="inherit">
                  <Icon sx={{ mr: '5px' }}>library_add</Icon>
                  <span>
                    {translateWithUserLocale(
                      'project.import_formatset(s)_button',
                    )}
                  </span>
                </Button>
              )
            }
            onCloseDialog={closeImportFormatsetsDialog}
            showImportFormatsetMessage={openImportFormatsetMessageDialog}
          />
          <OutdatedFormatsetsMessageDialog
            open={state.importFormatsetMessageOpen}
            onClose={closeImportFormatsetMessageDialog}
            invalidFormatsets={state.invalidFormatsetsText}
            type="import"
          />
          <Button
            size={'small'}
            onClick={handleClickAddSpecification}
            color="inherit"
            sx={{ marginLeft: '20px' }}>
            <Icon>add</Icon>
            <span>{translateWithUserLocale('project.add_formatset(s)')}</span>
          </Button>
          <Menu
            keepMounted={true}
            anchorEl={anchorEl}
            open={anchorEl != null}
            onClose={handleClosePlatformsMenu}>
            {props.platforms.map((platform: Platform) => {
              return (
                <FormatsetForm
                  key={platform.value}
                  activator={
                    props.project.status.name === 'archived' ? (
                      ''
                    ) : (
                      <MenuItem
                        onClick={() => handleSelectPlatform(platform.value)}>
                        {platform.label}
                      </MenuItem>
                    )
                  }
                  formatSetName={translateWithUserLocale(
                    'formatset_modal.default_formatset_name',
                  )}
                  platform={selectedPlatform}
                  restricted={false}
                  projectId={props.project.id}
                  onSave={async (formatsets: any) => {
                    props.onAddFormatsets(formatsets)
                  }}
                />
              )
            })}
          </Menu>
        </Box>
      )}

      <Paper sx={{ p: 2 }}>
        <Box
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginRight: '2px',
            alignItems: 'center',
            marginBottom: '25px',
            paddingTop: '18px',
          }}>
          <Box sx={{ paddingLeft: '18px' }}>
            <h3>{translateWithUserLocale('filter')}</h3>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">
              {translateWithUserLocale('labels.collapse_expand_filter')}
            </Typography>
            <CollapseButton collapsed={expanded} onClick={handleExpandClick} />
          </Box>
        </Box>

        <Collapse in={expanded} timeout="auto">
          <CreativeFilter
            filter={creativesFilter}
            project={props.project}
            platforms={props.platforms}
          />
        </Collapse>
        <SortableFormatsetComponent
          creativesFilter={creativesFilter.state}
          {...props}
        />
      </Paper>
    </div>
  )
}

const mapStateToProps = (state: State) => {
  return {
    makingCreative: state.creative.making,
  }
}

export default connect(mapStateToProps)(Main as any) as React.ComponentType<any>
